// @flow
import * as React from 'react';
import { connect } from 'react-redux';

import { Container, Row, Col } from 'reactstrap';

import Hero from 'components/Hero';
import Community from 'components/Community';
import Timeline from 'components/Timeline';
import EmergencyBreak from 'components/EmergencyBreak';

import {
  commentTopic,
  loadSingleTopic,
  deleteTopic,
  editTopic,
  editCommentTopic,
} from 'actions/topics';
import { addImage, deleteImage } from 'actions/images';

type Props = {
  // redux
  topic: ?SingleTopic,
  entries: Array<TimelineEntry>,
  onCommentTopic: (comment: string) => void,
  onEditCommentTopic: (index: number, text: string) => Promise<void>,
  onEditTopic: (field: string, value: string) => void,
  onLoadSingleTopic: () => void,
  onAddImage: (image: string) => void,
  onDeleteImage: (index: number) => void,
};

class TopicView extends React.Component<Props> {
  componentDidMount() {
    const { onLoadSingleTopic } = this.props;
    onLoadSingleTopic();
  }

  _onDelete = async reason => {
    const { onDeleteTopic, onLoadSingleTopic } = this.props;
    await onDeleteTopic(reason);
    await onLoadSingleTopic();
  };

  _onAddImage = async image => {
    const { onAddImage, onLoadSingleTopic } = this.props;
    await onAddImage(image);
    await onLoadSingleTopic();
  };

  _onDeleteImage = async index => {
    const { onDeleteImage, onLoadSingleTopic } = this.props;
    await onDeleteImage(index);
    await onLoadSingleTopic();
  };

  _onFieldChange = async (field, value) => {
    const { onEditTopic, onLoadSingleTopic } = this.props;
    await onEditTopic(field, value);
    await onLoadSingleTopic();
  };

  _onEditComment = async (index, text) => {
    const { onEditCommentTopic, onLoadSingleTopic } = this.props;
    await onEditCommentTopic(index, text);
    await onLoadSingleTopic();
  };

  render() {
    const { topic, entries, onCommentTopic, onLoadSingleTopic } = this.props;
    if (!topic) {
      return null;
    }
    const {
      title,
      description,
      images,
      createdAt,
      createdBy,
      visibleIn,
      deleted,
    } = topic;
    return (
      <Container fluid>
        <Row>
          <Col>
            <Hero
              title={title}
              description={description}
              images={images}
              createdAt={createdAt}
              createdBy={{
                ...createdBy,
                isCreatedByManager: true,
              }}
              onImageAdded={this._onAddImage}
              onImageDeleted={this._onDeleteImage}
              onFieldChanged={this._onFieldChange}
              maxImages={4}
            />
            <Timeline
              entries={entries}
              editable={!deleted}
              onComment={onCommentTopic}
              onReloadData={onLoadSingleTopic}
              onCommentEdited={this._onEditComment}
            />
          </Col>
          <Col xs="12" sm="12" md="12" lg="5" xl="5">
            <Community communities={Object.values(visibleIn)} />
            {!deleted && (
              <EmergencyBreak type="Thema" onDelete={this._onDelete} />
            )}
          </Col>
        </Row>
      </Container>
    );
  }
}

const mapStateToProps = (
  {
    auth: {
      user: { userId },
    },
    topics: { singles: topics },
  },
  props
) => {
  const { topicId } = props.match.params;
  const topic = topics[topicId];
  const entries = topic
    ? [
        ...topic.comments.map((c, index) => ({
          type: 'comment',
          createdByEmail: c.createdByEmail,
          createdByName: `${c.firstname} ${c.lastname}`,
          isCreatedByManager: c.isCreatedByManager,
          text: c.text,
          createdAt: c.createdAt,
          isOwnEntry: c.userId === userId,
          commentIndex: index,
        })),
        ...topic.likes.map(l => ({
          type: 'like',
          createdByEmail: l.createdByEmail,
          createdByName: `${l.firstname} ${l.lastname}`,
          isCreatedByManager: l.isCreatedByManager,
          createdAt: l.createdAt,
        })),
        ...(!!topic.deleted
          ? [
              {
                type: 'state',
                isCreatedByManager: true,
                createdAt: topic.deleted.createdAt,
                text: 'deleted',
              },
            ]
          : []),
      ].sort((a, b) => a.createdAt - b.createdAt)
    : [];

  return {
    topic,
    entries,
  };
};
const mapDispatchToProps = (dispatch, props) => {
  const { topicId } = props.match.params;
  return {
    onDeleteTopic: reason => dispatch(deleteTopic(topicId, reason)),
    onCommentTopic: text => dispatch(commentTopic(topicId, text)),
    onEditCommentTopic: (index, text) =>
      dispatch(editCommentTopic(topicId, index, text)),
    onEditTopic: (field, value) => dispatch(editTopic(topicId, field, value)),
    onLoadSingleTopic: () => dispatch(loadSingleTopic(topicId)),
    onAddImage: image => dispatch(addImage('topic', topicId, image)),
    onDeleteImage: index => dispatch(deleteImage('topic', topicId, index)),
  };
};

const TopicViewContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(TopicView);

export default TopicViewContainer;



// WEBPACK FOOTER //
// ./src/views/Topic/Topic.js